import { useId, type FC } from "react";
import { useAtom, type PrimitiveAtom } from "jotai";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { DEFAULT_WORKFLOW_LIMITS } from "@/utils/automations";

type WorkflowEditActionProps = {
    maxParallelAtom: PrimitiveAtom<number | undefined>;
    maxTotalAtom: PrimitiveAtom<number | undefined>;
};

export const WorkflowEditAction: FC<WorkflowEditActionProps> = ({ maxParallelAtom, maxTotalAtom }) => {
    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-4">
                    <MaxParallelInput maxParallelAtom={maxParallelAtom} />
                    <MaxTotalInput maxTotalAtom={maxTotalAtom} />
                </div>
                <div className="text-sm text-content-secondary">
                    Note: These limits are currently restricted during beta to ensure system stability. Higher limits
                    will be available after the beta period.
                </div>
            </div>
        </div>
    );
};

type MaxParallelInputProps = {
    maxParallelAtom: PrimitiveAtom<number | undefined>;
};

const MaxParallelInput: FC<MaxParallelInputProps> = ({ maxParallelAtom }) => {
    const id = useId();
    const [maxParallel, setMaxParallel] = useAtom(maxParallelAtom);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setMaxParallel(value === "" ? undefined : parseInt(value, 10));
    };

    return (
        <InputField
            label="Max concurrent actions"
            hint="The number of actions that can run in parallel per execution"
            id={id}
        >
            <Input
                id={id}
                type="number"
                min={1}
                max={25}
                value={maxParallel ?? ""}
                onChange={handleChange}
                placeholder={String(DEFAULT_WORKFLOW_LIMITS.maxParallel)}
                className="max-w-[300px] bg-transparent"
                data-testid="max-parallel-input"
            />
        </InputField>
    );
};

type MaxTotalInputProps = {
    maxTotalAtom: PrimitiveAtom<number | undefined>;
};

const MaxTotalInput: FC<MaxTotalInputProps> = ({ maxTotalAtom }) => {
    const id = useId();
    const [maxTotal, setMaxTotal] = useAtom(maxTotalAtom);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setMaxTotal(value === "" ? undefined : parseInt(value, 10));
    };

    return (
        <InputField
            label="Max total actions"
            hint="The total number of actions that can be created by a single execution"
            id={id}
        >
            <Input
                id={id}
                type="number"
                min={1}
                max={100}
                value={maxTotal ?? ""}
                onChange={handleChange}
                placeholder={String(DEFAULT_WORKFLOW_LIMITS.maxTotal)}
                className="max-w-[300px] bg-transparent"
                data-testid="max-total-input"
            />
        </InputField>
    );
};
